import React, { CSSProperties, MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl, { FlyToOptions, Map as MapBoxMap } from 'mapbox-gl';
import matchMedia from 'matchmediaquery';

declare global {
    interface Window {
        fullScreenControl: InstanceType<typeof mapboxgl.FullscreenControl>;
        resizeTime: number;
    }
}

interface MapV2Type {
    latitude: number;
    longitude: number;
    zoom?: number;
    mapboxAccessToken: string;
    initialLongitude?: number;
    initialLatitude?: number;
    initialZoom?: number;
    enableDragRotate?: boolean;
    enablePan?: boolean;
    enableZoom?: boolean;
    showFullscreenControl?: boolean;
    showNavigationControl?: boolean;
    showCenterMarker?: boolean;
    centerMarkerText: string;
    centerMarkerCircleColor: string;
    centerMarkerTextColor: string;
    centerMarkerLatitude?: number;
    centerMarkerLongitude?: number;
    smallPinImageUrl?: string;
    bigPinImageUrl?: string;
    height?: number;
    extension?: (
        map: InstanceType<typeof MapBoxMap> | null,
        latitude: number,
        longitude: number,
        mapZoomOutPressed: () => void,
        mapZoomInPressed: () => void,
        mapPanToMarkerPressed: () => void,
        zoom: number,
        changeZoom: (zoom: number) => void
    ) => ReactNode;
    onZoomChanged?: (value: number) => void;
    onPanned?: (latitude: number, longitude: number) => void;
    isBot?: boolean;
    mapRef?: MutableRefObject<unknown>;
    adRefreshActionOnPresetZoom: () => void;
    adRefreshActionOnPlusButtonClickZoomIn: (zoomLevel: number) => void;
    adRefreshActionOnMinusButtonClickZoomOut: (zoomLevel: number) => void;
    adRefreshActionClickPanToMarkerButton: () => void;
}

export const MapV2 = ({
    latitude,
    longitude,
    zoom,
    mapboxAccessToken,
    initialLongitude,
    initialLatitude,
    initialZoom = 10,
    enableDragRotate,
    enablePan,
    enableZoom,
    showFullscreenControl,
    showNavigationControl,
    showCenterMarker,
    centerMarkerText,
    centerMarkerCircleColor,
    centerMarkerTextColor,
    centerMarkerLatitude,
    centerMarkerLongitude,
    smallPinImageUrl,
    bigPinImageUrl,
    height,
    extension,
    onZoomChanged,
    onPanned,
    isBot,
    mapRef,
    adRefreshActionOnPlusButtonClickZoomIn,
    adRefreshActionOnMinusButtonClickZoomOut,
    adRefreshActionClickPanToMarkerButton
}: MapV2Type) => {
    const initialLoad = useRef<boolean>(false);
    const componentMap = useRef<InstanceType<typeof MapBoxMap> | null>(null);
    const mapContainer = useRef<HTMLDivElement>(null);
    const centerMarker = useRef<InstanceType<typeof mapboxgl.Marker> | null>(null);
    const centerMarkerSpan = useRef<HTMLSpanElement | undefined>();
    const navigationControl = useRef<InstanceType<typeof mapboxgl.NavigationControl> | null>();
    const fullScreenControl = useRef<InstanceType<typeof mapboxgl.FullscreenControl>>();
    const [ currentProps, setCurrentProps ] = useState<Record<string, unknown>>({
        showFullscreenControl,
        showNavigationControl,
        showCenterMarker,
        centerMarkerCircleColor,
        centerMarkerText,
        centerMarkerTextColor,
        zoom,
        latitude,
        longitude,
        enableDragRotate,
        enablePan,
        enableZoom
    });

    function onMapPanned() {
        if (onPanned) {
            const { lat, lng } = componentMap?.current?.getCenter() as mapboxgl.LngLat;
            onPanned(lat, lng);
        }
    }

    function onMapZoomChanged() {
        if (onZoomChanged) {
            const zoom = componentMap?.current?.getZoom();
            if (Number.isFinite(zoom)) {
                onZoomChanged(zoom!);
            }
        }
    }

    function tryLocationUpdate(latitude: number, longitude: number, zoom: number | undefined, showCenterMarker: boolean | undefined) {
        if (latitude && Number.isFinite(latitude) && longitude && Number.isFinite(longitude)) {
            if (componentMap?.current) {
                if (showCenterMarker) {
                    createMarker(
                        centerMarkerLatitude || latitude,
                        centerMarkerLongitude || longitude,
                        centerMarkerText,
                        centerMarkerCircleColor,
                        centerMarkerTextColor
                    );
                }
                const flyToParams: FlyToOptions = { center: [longitude, latitude] };
                if (zoom && Number.isFinite(zoom)) {
                    flyToParams.zoom = zoom;
                }
                componentMap?.current.flyTo(flyToParams);
            }
        }
    }

    function createMarker(latitude: number, longitude: number, markerText: string, circleColor: string, markerTextColor: string) {
        if (!componentMap?.current) {
            return;
        }
        if (centerMarker.current) {
            centerMarker.current.remove();
            centerMarkerSpan.current = undefined;
        }
        const el = document.createElement('div');
        el.className = 'marker';
        let imageSrc, width, height;

        if (matchMedia('(max-width: 480px)').matches) {
            imageSrc = smallPinImageUrl;
            width = '24px';
            height = '34px';
        } else {
            imageSrc = bigPinImageUrl;
            width = '37px';
            height = '51px';
        }
        const img = document.createElement('img');
        if (imageSrc) {
            img.src = imageSrc;
            img.style.width = width;
            img.style.height = height;
            img.alt = 'Map pin';
            el.appendChild(img);
        }

        if (markerText) {
            centerMarkerSpan.current = document.createElement('span');
            centerMarkerSpan.current.innerText = markerText;
            centerMarkerSpan.current.style.position = 'absolute';

            if (markerTextColor) {
                centerMarkerSpan.current.style.color = markerTextColor;
            }

            if (circleColor) {
                centerMarkerSpan.current.style.border = `1px ${circleColor} solid`;
                centerMarkerSpan.current.style.borderRadius = '100%';
                centerMarkerSpan.current.style.textAlign = 'center';
            }

            if (matchMedia('(max-width: 480px)').matches) {
                centerMarkerSpan.current.style.top = '1px';
                centerMarkerSpan.current.style.left = '2px';
                centerMarkerSpan.current.style.padding = '0px';
                centerMarkerSpan.current.style.paddingLeft = '3px';
                centerMarkerSpan.current.style.paddingRight = '3px';
                centerMarkerSpan.current.style.fontSize = '11px';
                centerMarkerSpan.current.style.minWidth = '12px';
            } else {
                centerMarkerSpan.current.style.top = '3px';
                centerMarkerSpan.current.style.left = '3px';
                centerMarkerSpan.current.style.padding = '5px';
                centerMarkerSpan.current.style.fontSize = '16px';
                centerMarkerSpan.current.style.minWidth = '17px';
            }

            el.appendChild(centerMarkerSpan.current);
        }

        centerMarker.current = new mapboxgl.Marker(el).setLngLat([longitude, latitude]).addTo(componentMap?.current);
    }

    function addFullscreenControl() {
        fullScreenControl.current = new mapboxgl.FullscreenControl();
        window.fullScreenControl = fullScreenControl.current;
        window.resizeTime = 100;

        // @ts-expect-error Override the fullscreen button click to trigger a resize to fix the safari fullscreen button issue.
        const originalOnClickFullScreen = fullScreenControl?.current?._onClickFullscreen;
        // @ts-expect-error JMJIII 4.5.23: We suspect that this functionality is no longer being used. 
        fullScreenControl.current._onClickFullscreen = () => {
            originalOnClickFullScreen();
            setTimeout(() => {
                try {
                    if (componentMap.current 
                        && componentMap.current?.getContainer?.() 
                        && document.body.contains(componentMap.current?.getContainer?.())) {
                        componentMap.current?.resize?.();
                    }
                } catch (error) {
                    console.error(error);   
                }
            }, window.resizeTime);
        };

        componentMap?.current?.addControl?.(fullScreenControl.current, 'bottom-right');
    }

    const changeZoom = useCallback((zoom: number) => {
        componentMap?.current?.zoomTo(zoom);
    }, [zoom]);

    function mapPanToMarkerPressed() {
        if (centerMarker?.current) {
            const { lng, lat } = centerMarker?.current?.getLngLat() || {};
            componentMap?.current?.easeTo({ center: [lng, lat] });
        } else {
            componentMap?.current?.easeTo({ center: [longitude, latitude] });
        }

        adRefreshActionClickPanToMarkerButton?.();
    }

    function mapZoomInPressed() {
        componentMap?.current?.zoomIn();
        const zoomLevel = componentMap?.current?.getZoom() as number;
        adRefreshActionOnPlusButtonClickZoomIn?.(zoomLevel);
    }

    function mapZoomOutPressed() {
        componentMap?.current?.zoomOut();
        const zoomLevel = componentMap?.current?.getZoom() as number;
        adRefreshActionOnMinusButtonClickZoomOut?.(zoomLevel);
    }

    function renderMapExtension() {
        if (!extension) {
            return null;
        }

        if (isBot && typeof zoom === 'number') {
            // we still want to load extension, but without mapbox object
            return extension(
                null,
                latitude,
                longitude,
                mapZoomOutPressed,
                mapZoomInPressed,
                mapPanToMarkerPressed,
                zoom,
                changeZoom
            );
        }

        if (componentMap?.current) {
            return extension(
                componentMap?.current,
                latitude,
                longitude,
                mapZoomOutPressed,
                mapZoomInPressed,
                mapPanToMarkerPressed,
                zoom!,
                changeZoom
            );
        }
        return null;
    }

    useEffect(() => {
        return () => {
            componentMap?.current?.remove?.();
        };
    }, []);

    useEffect(() => {
        if (!initialLoad.current 
            && mapboxAccessToken
            && mapContainer.current 
            && typeof initialLongitude === 'number' 
            && Math.abs(initialLongitude) >= 0 
            && typeof initialLatitude === 'number' 
            && Math.abs(initialLatitude) >= 0 
        ) {
            mapboxgl.accessToken = mapboxAccessToken;
            const center = [initialLongitude, initialLatitude] as mapboxgl.LngLatLike;

            componentMap.current = isBot ? null : new mapboxgl.Map({
                container: mapContainer.current,
                center,
                zoom: initialZoom,
                minZoom: 2,
                maxZoom: 15,
                style: 'mapbox://styles/mapbox/standard',
                touchZoomRotate: enableZoom || false,
                scrollZoom: enableZoom,
                logoPosition: 'bottom-left',
                attributionControl: true,
                projection: 'mercator'
            });

            if (mapRef) {
                mapRef.current = componentMap?.current;
            }

            setTimeout(() => {
                try {
                    if (componentMap.current 
                        && componentMap.current?.getContainer?.() 
                        && document.body.contains(componentMap.current?.getContainer?.())) {
                        componentMap.current?.resize?.();
                    }
                } catch (error) {
                    console.error(error);   
                }
            }, 500);

            componentMap?.current?.on('dragend', onMapPanned);
            componentMap?.current?.on('zoomend', onMapZoomChanged);
            tryLocationUpdate(latitude, longitude, zoom, showCenterMarker);

            if (!enableDragRotate) {
                componentMap?.current?.dragRotate.disable();
            }

            if (!enablePan) {
                componentMap?.current?.dragPan.disable();
            }

            if (!enableZoom) {
                componentMap?.current?.scrollZoom.disable();
                componentMap?.current?.boxZoom.disable();
                componentMap?.current?.doubleClickZoom.disable();
                componentMap?.current?.touchZoomRotate.disable();
            }

            if (showFullscreenControl) {
                addFullscreenControl();
            }

            if (showNavigationControl) {
                navigationControl.current = new mapboxgl.NavigationControl();
                componentMap?.current?.addControl(navigationControl.current, 'bottom-right');
            }

            if (showCenterMarker && (Number.isFinite(latitude) && Number.isFinite(longitude) || Number.isFinite(centerMarkerLatitude) && Number.isFinite(centerMarkerLongitude))) {
                const markerLat = centerMarkerLatitude || latitude;
                const markerLon = centerMarkerLongitude || longitude;
                createMarker(
                    markerLat,
                    markerLon,
                    centerMarkerText,
                    centerMarkerCircleColor,
                    centerMarkerTextColor
                );
            }

            initialLoad.current = true;
        }      
    }, [initialLongitude, 
        initialLatitude, 
        initialZoom, 
        centerMarkerLatitude, 
        centerMarkerLongitude, 
        mapRef, 
        isBot, 
        mapboxAccessToken, 
        enableZoom, 
        enableDragRotate, 
        enablePan, 
        showFullscreenControl, 
        latitude, 
        longitude, 
        zoom, 
        showCenterMarker,
        showNavigationControl,
        centerMarkerText,
        centerMarkerCircleColor,
        centerMarkerTextColor
    ]);

    useEffect(() => {
        if (componentMap.current) {
            const nextLatitude = latitude;
            const nextLongitude = longitude;
            const nextZoom = zoom;
            const currentZoom = currentProps?.zoom;

            const locationChanged =            Number.isFinite(nextLatitude) && currentProps?.latitude !== nextLatitude
            || Number.isFinite(nextLongitude) && currentProps?.longitude !== nextLongitude;

            if (locationChanged || nextZoom && currentZoom !== nextZoom) {
                tryLocationUpdate(
                    nextLatitude,
                    nextLongitude,
                    nextZoom,
                    showCenterMarker
                );
            }

            if (currentProps?.enableDragRotate !== enableDragRotate) {
                setCurrentProps({ ...currentProps,  enableDragRotate: enableDragRotate });
                if (enableDragRotate) {
                    componentMap?.current?.dragRotate?.enable();
                } else {
                    componentMap?.current?.dragRotate?.disable();
                }
            }

            if (currentProps?.enablePan !== enablePan) {
                setCurrentProps({ ...currentProps, enablePan: enablePan });
                if (enablePan) {
                    componentMap?.current?.dragPan?.enable();
                } else {
                    componentMap?.current?.dragPan?.disable();
                }
            }

            if (currentProps?.enableZoom !== enableZoom) {
                setCurrentProps({ ...currentProps,  enableZoom: enableZoom });
                if (enableZoom) {
                    componentMap?.current?.scrollZoom?.enable();
                    componentMap?.current?.boxZoom?.enable();
                    componentMap?.current?.doubleClickZoom?.enable();
                    componentMap?.current?.touchZoomRotate?.enable();
                } else {
                    componentMap?.current?.scrollZoom?.disable();
                    componentMap?.current?.boxZoom?.disable();
                    componentMap?.current?.doubleClickZoom?.disable();
                    componentMap?.current?.touchZoomRotate?.disable();
                }
            }

            if (showFullscreenControl !== currentProps?.showFullscreenControl) {
                if (showFullscreenControl) {
                    addFullscreenControl();
                } else {
                    if (fullScreenControl?.current) {
                        componentMap?.current?.removeControl(fullScreenControl.current);
                        fullScreenControl.current = undefined;
                    }
                }
            }

            if (showNavigationControl !== currentProps?.showNavigationControl) {
                if (showNavigationControl) {
                    navigationControl.current = new mapboxgl.NavigationControl();
                    componentMap?.current?.addControl(navigationControl.current, 'bottom-right');
                } else {
                    if (navigationControl.current) {
                        componentMap?.current?.removeControl(navigationControl.current);
                        navigationControl.current = undefined;
                    }
                    
                }
            }

            if (showCenterMarker !== currentProps?.showCenterMarker) {
                if (
                    showCenterMarker
                    && (Number.isFinite(nextLatitude) && Number.isFinite(nextLongitude)
                    || Number.isFinite(centerMarkerLatitude)
                        && Number.isFinite(centerMarkerLongitude))
                ) {
                    const markerLat = centerMarkerLatitude || nextLatitude;
                    const markerLon = centerMarkerLongitude || nextLongitude;

                    createMarker(
                        markerLat,
                        markerLon,
                        centerMarkerText,
                        centerMarkerCircleColor,
                        centerMarkerTextColor
                    );
                } else {
                    centerMarker?.current?.remove();
                    centerMarkerSpan.current = undefined;
                }
            }

            if (centerMarkerCircleColor && centerMarkerCircleColor !== currentProps?.centerMarkerCircleColor && centerMarkerSpan.current) {
                centerMarkerSpan.current.style.border = `1px ${centerMarkerCircleColor} solid`;
            }

            if (centerMarkerText && centerMarkerText !== currentProps?.centerMarkerText && centerMarkerSpan?.current) {
                centerMarkerSpan.current.innerText = centerMarkerText;
            }

            if (centerMarkerTextColor && centerMarkerTextColor !== currentProps?.centerMarkerTextColor && centerMarkerSpan?.current) {
                centerMarkerSpan.current.style.color = centerMarkerTextColor;
            }
        }

        setCurrentProps ({
            showFullscreenControl,
            showNavigationControl,
            showCenterMarker,
            centerMarkerCircleColor,
            centerMarkerText,
            centerMarkerTextColor,
            zoom,
            latitude,
            longitude,
            enableDragRotate,
            enablePan,
            enableZoom
        });
    }, [
        showFullscreenControl,
        showNavigationControl,
        showCenterMarker,
        centerMarkerCircleColor,
        centerMarkerText,
        centerMarkerTextColor,
        zoom,
        latitude,
        longitude,
        enableDragRotate,
        enablePan,
        enableZoom,
        currentProps.showFullscreenControl,
        currentProps.showNavigationControl,
        currentProps.showCenterMarker,
        currentProps.centerMarkerCircleColor,
        currentProps.centerMarkerText,
        currentProps.centerMarkerTextColor,
        currentProps.zoom,
        currentProps.latitude,
        currentProps.longitude,
        currentProps.enableDragRotate,
        currentProps.enablePan,
        currentProps.enableZoom      
    ]);

    return (
        <div style={{ ...container, height: height }}>
            <div style={mapStyle} ref={mapContainer} />
            {renderMapExtension()}
        </div>
    );
    
};

const container: CSSProperties = {
    width: '100%',
    background: '#F9F9F9',
    border: '1px solid #EEEEEE',
    borderRadius: '2px',
    display: 'flex',
    position: 'relative'
};
    
const mapStyle: CSSProperties = {
    height: '100%',
    width: '100%'
};